import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import ScrollSpy from 'react-ui-scrollspy';
import { useLocation } from 'react-router-dom';

import { useFetchCampaign } from '../hooks/useFetchCampaign';
import { useSteps } from '../hooks/useSteps';
import { StepContainer } from './StepContainer/StepContainer.jsx';
import { Navbar } from './Shared/Navbar.jsx';
import { CampaignEditNavigation } from './CampaignEditNavigation.jsx';
import { PrizeCta } from './Prizes/PrizeCta.jsx';

import { ScrollContextProvider } from '../contexts/ScrollContext.jsx';
import { CampaignContextProvider } from '../contexts/CampaignContextProvider.jsx';
import { POSTS, PRIZES, BANNER, DATE } from '../utils/constants';
import useLocalStorage from '../../../lib/hooks/useLocalStorage';
import { RESTRICTED_SITE_ADMIN } from '../../../lib/utils/constants/UserTypes';
import LoadingComponent from '../../../shared/LoadingComponent/LoadingComponent.jsx';

export const CampaignEditor = ({ id }) => {
  const { search } = useLocation();
  const scrollPosition = useSelector(state => state.campaignScroll.edit);
  const { campaign } = useFetchCampaign({
    id,
    forceRefetch: true,
    scrollPosition,
    page: 'edit',
  });
  const [activeStep, setActiveStep] = useState(null);
  const { steps } = useSteps();
  const { userType } = useSelector(state => state.currentUser);
  const postStep = steps[steps.length - 1];
  const [scrollToPostStep, setScrollToPostStep] = useLocalStorage(
    `post-step-${id}`,
    false,
  );

  useEffect(() => {
    if (activeStep === POSTS && !scrollToPostStep) {
      setScrollToPostStep(true);
    }
  }, [activeStep, campaign.uuid, scrollToPostStep, setScrollToPostStep]);

  useEffect(() => {
    if (search && campaign.uuid) {
      const searchParams = new URLSearchParams(search);
      const postId = searchParams.get('postId');
      const tab = searchParams.get('tab');

      switch (tab) {
        case 'prizes':
          document.getElementById(PRIZES).scrollIntoView();
          break;
        case 'banner':
          document.getElementById(BANNER).scrollIntoView();
          break;
        case 'date':
          document.getElementById(DATE).scrollIntoView();
          break;
        case 'tiles':
          document.getElementById(POSTS).scrollIntoView();
          break;
        default:
          break;
      }

      if (postId) {
        setTimeout(() => {
          document.getElementById(postId).scrollIntoView();
        }, 1000);
      }
    }
  }, [campaign.uuid, search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const tab = searchParams.get('tab');
    if (
      campaign.uuid &&
      scrollPosition === null &&
      postStep.ref.current &&
      scrollToPostStep &&
      !tab
    ) {
      const yPosition = postStep.ref.current.getBoundingClientRect().y;

      window.scrollTo(0, yPosition);
    }
  }, [postStep.ref, scrollPosition, campaign.uuid, scrollToPostStep, search]);

  if (!campaign.uuid) return <LoadingComponent />;

  const isDisabled = campaign.isArchived || !!campaign.deletedAt;

  return (
    <>
      <Navbar id={id} mode="edit" isDeleted={!!campaign.deletedAt}>
        <CampaignEditNavigation steps={steps} activeStep={activeStep} />
      </Navbar>
      <CampaignContextProvider campaign={campaign}>
        <ScrollContextProvider posts={campaign.posts}>
          {campaign.prize.id &&
            userType !== RESTRICTED_SITE_ADMIN &&
            !isDisabled && (
              <PrizeCta prize={campaign.prize} posts={campaign.posts} />
            )}
          <Container>
            <ScrollSpy
              updateHistoryStack={false}
              scrollThrottle={50}
              offsetBottom={360}
              onUpdateCallback={stepId => setActiveStep(stepId)}
            >
              {steps.map(({ Component, ref, props }, index) => {
                const nextStep = steps[index + 1];
                return (
                  <StepContainer
                    key={props.id}
                    stepNumber={index + 1}
                    ref={ref}
                    nextStep={nextStep}
                    {...props}
                  >
                    <Component campaign={campaign} nextStep={nextStep} />
                  </StepContainer>
                );
              })}
            </ScrollSpy>
          </Container>
        </ScrollContextProvider>
      </CampaignContextProvider>
    </>
  );
};

const Container = styled.div`
  position: relative;
  margin: 24px;
`;
